// Shared YouTube (IFrame API + nocookie fallback) and Vimeo embeds — avoids Error 153; see fullcol-video.js / site-header meta referrer.

export const IFRAME_ALLOW =
  "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";

/** @type {Promise<void> | null} */
let youtubeIframeApiPromise = null;

export function ensureYoutubeIframeApi() {
  if (window.YT?.Player) {
    return Promise.resolve();
  }
  if (!youtubeIframeApiPromise) {
    youtubeIframeApiPromise = new Promise((resolve) => {
      const tryResolve = () => {
        if (window.YT?.Player) resolve();
      };
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = function () {
        if (typeof prev === "function") prev();
        tryResolve();
      };
      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScript = document.getElementsByTagName("script")[0];
        firstScript.parentNode.insertBefore(tag, firstScript);
      }
      const poll = setInterval(() => {
        if (window.YT?.Player) {
          clearInterval(poll);
          resolve();
        }
      }, 50);
      setTimeout(() => clearInterval(poll), 25000);
    });
  }
  return youtubeIframeApiPromise;
}

export function buildYoutubeEmbedUrl(videoId, useNoCookie = false) {
  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    loop: "1",
    playlist: videoId,
    controls: "0",
    rel: "0",
    playsinline: "1",
    modestbranding: "1",
    enablejsapi: "1",
    origin: window.location.origin,
    widget_referrer: window.location.href,
  });
  const host = useNoCookie ? "https://www.youtube-nocookie.com" : "https://www.youtube.com";
  return `${host}/embed/${encodeURIComponent(videoId)}?${params}`;
}

// Backward-compatible export used by existing components.
export function buildYoutubeNoCookieEmbedUrl(videoId) {
  return buildYoutubeEmbedUrl(videoId, true);
}

/**
 * @param {HTMLElement} container
 * @param {string} videoId
 * @param {string} embedClassNames - classes for YT host div and fallback iframe
 */
export function appendYoutubeIframeFallback(container, videoId, embedClassNames) {
  const el = document.createElement("iframe");
  el.className = embedClassNames;
  el.setAttribute("referrerpolicy", "origin-when-cross-origin");
  el.setAttribute("allow", IFRAME_ALLOW);
  el.setAttribute("allowfullscreen", "");
  el.setAttribute("title", "YouTube video player");
  // Safari can be stricter with no-cookie + referrer combinations; prefer standard host here.
  el.src = buildYoutubeEmbedUrl(videoId, false);
  container.appendChild(el);
  return el;
}

/**
 * @param {HTMLElement} container
 * @param {string} videoId
 * @param {string} embedClassNames
 * @returns {Promise<HTMLElement>}
 */
export function createYoutubeEmbed(container, videoId, embedClassNames) {
  return new Promise((resolve) => {
    let settled = false;
    const origin = window.location.origin;
    const playerDiv = document.createElement("div");
    const domId = `yt-embed-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    playerDiv.id = domId;
    playerDiv.className = embedClassNames;
    container.appendChild(playerDiv);

    const finish = (el) => {
      if (settled) return;
      settled = true;
      clearTimeout(apiTimeout);
      resolve(el);
    };

    const useFallback = () => {
      if (settled) return;
      playerDiv.remove();
      finish(appendYoutubeIframeFallback(container, videoId, embedClassNames));
    };

    const apiTimeout = setTimeout(useFallback, 12000);

    ensureYoutubeIframeApi()
      .then(() => {
        if (settled) return;
        if (!window.YT?.Player) {
          useFallback();
          return;
        }
        try {
          new YT.Player(domId, {
            host: "https://www.youtube.com",
            videoId,
            playerVars: {
              autoplay: 1,
              mute: 1,
              loop: 1,
              playlist: videoId,
              controls: 0,
              rel: 0,
              modestbranding: 1,
              playsinline: 1,
              enablejsapi: 1,
              origin,
              widget_referrer: window.location.href,
            },
            events: {
              onReady: () => finish(playerDiv),
              onError: (e) => {
                console.warn("YouTube player error:", e?.data);
                useFallback();
              },
            },
          });
        } catch (err) {
          console.warn("YouTube IFrame API init:", err);
          useFallback();
        }
      })
      .catch(() => {
        useFallback();
      });
  });
}

/** iOS / iPadOS Safari (and desktop Safari with touch) — background=1 embeds often fail to autoplay. */
function isAppleTouchDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/**
 * Vimeo player URL: chromeless on desktop (background), safer params on Apple touch Safari.
 */
export function buildVimeoEmbedUrl(videoId) {
  const id = encodeURIComponent(String(videoId).trim());
  const params = new URLSearchParams({
    autoplay: "1",
    muted: "1",
    loop: "1",
    playsinline: "1",
    api: "1",
  });
  if (isAppleTouchDevice()) {
    params.set("controls", "0");
    params.set("title", "0");
    params.set("byline", "0");
    params.set("portrait", "0");
  } else {
    params.set("background", "1");
  }
  return `https://player.vimeo.com/video/${id}?${params}`;
}

/**
 * @param {HTMLElement} container
 * @param {string} videoId
 * @param {string} embedClassNames
 * @returns {HTMLIFrameElement}
 */
export function appendVimeoEmbed(container, videoId, embedClassNames) {
  const el = document.createElement("iframe");
  el.className = embedClassNames;
  el.setAttribute("referrerpolicy", "origin-when-cross-origin");
  el.setAttribute("allow", "autoplay; fullscreen; picture-in-picture; encrypted-media; web-share");
  el.setAttribute("allowfullscreen", "");
  el.setAttribute("title", "Vimeo video player");
  el.src = buildVimeoEmbedUrl(videoId);
  container.appendChild(el);
  return el;
}

/**
 * MP4, YouTube, or Vimeo as a full-bleed cover layer (shared by hero + fullcol blocks).
 * Embeds use `{baseClass} {baseClass}--embed` (see `.fullwidth-video__video--embed` in SCSS).
 *
 * @param {HTMLElement} container
 * @param {string} videoType - mp4 | youtube | vimeo
 * @param {{ src?: string, id?: string }} sources
 * @param {string} baseClass - e.g. fullwidth-video__video or hero-shared-bg__video
 * @returns {Promise<HTMLElement | null>}
 */
export function injectCoverVideoEmbed(container, videoType, sources, baseClass) {
  const type = (videoType || "mp4").toLowerCase();
  const src = sources.src || "";
  const id = (sources.id || "").trim();
  const embedClassNames = `${baseClass} ${baseClass}--embed`;

  if (type === "mp4" && src) {
    const el = document.createElement("video");
    el.className = baseClass;
    el.src = src;
    el.muted = true;
    el.loop = true;
    el.playsInline = true;
    el.setAttribute("playsinline", "");
    el.autoplay = true;
    container.appendChild(el);
    return Promise.resolve(el);
  }
  if (type === "youtube" && id) {
    return createYoutubeEmbed(container, id, embedClassNames);
  }
  if (type === "vimeo" && id) {
    return Promise.resolve(appendVimeoEmbed(container, id, embedClassNames));
  }
  return Promise.resolve(null);
}
